import { useState, useEffect } from 'react';
import { useJobs, Job } from '@/context/JobContext';
import { Loader2, Trash2, CheckCircle, AlertCircle, ChevronUp, ChevronDown, Activity, Clock } from 'lucide-react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import api, { ProcurementJob } from '@/lib/api';

export default function GlobalSpinner() {
    const { activeJobsCount, jobs, removeJob, updateJobStatus } = useJobs();
    const [isExpanded, setIsExpanded] = useState(false);

    // Auto-expand checks or specific logic could go here, but keeping it simple.

    if (jobs.length === 0) return null;

    const toggleExpand = () => setIsExpanded(!isExpanded);

    return createPortal(
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2 animate-in slide-in-from-bottom-5 duration-300 font-sans">

            {/* Expanded List Modal */}
            {isExpanded && (
                <div className="bg-card text-card-foreground p-0 mb-2 w-80 md:w-96 rounded-xl shadow-2xl border border-border overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                    <div className="p-4 border-b border-border bg-muted/50 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <Activity className="w-4 h-4 text-primary" />
                            <h3 className="font-bold text-sm">Background Jobs</h3>
                        </div>
                        <span className="text-xs text-muted-foreground font-mono bg-background px-2 py-0.5 rounded-full border border-border">
                            {activeJobsCount} Active
                        </span>
                    </div>
                    <div className="max-h-[300px] overflow-y-auto p-2 space-y-2">
                        {jobs.map((job) => (
                            <JobItem
                                key={job.id}
                                job={job}
                                onDelete={() => removeJob(job.id)}
                                onUpdate={updateJobStatus}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Floating Trigger Pill */}
            <button
                onClick={toggleExpand}
                className={cn(
                    "bg-foreground text-background px-4 py-3 rounded-xl shadow-xl flex items-center gap-3 transition-all hover:opacity-90 active:scale-95",
                    isExpanded ? "bg-muted-foreground" : "bg-foreground"
                )}
            >
                {activeJobsCount > 0 ? (
                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                ) : (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                )}

                <div className="flex flex-col items-start mr-2">
                    <span className="text-sm font-semibold whitespace-nowrap">
                        {activeJobsCount === 0 ? 'All tasks finished' : `${activeJobsCount} background task${activeJobsCount !== 1 ? 's' : ''} running...`}
                    </span>
                </div>

                {isExpanded ? <ChevronDown className="w-5 h-5 font-bold" /> : <ChevronUp className="w-5 h-5 font-bold" />}
            </button>
        </div>,
        document.body
    );
}

function procurementStatusToUi(status: ProcurementJob["status"]): Job["status"] {
    if (status === "completed") return "completed";
    if (status === "failed") return "failed";
    if (status === "cancelled") return "cancelled";
    return "running";
}

function JobItem({
    job,
    onDelete,
    onUpdate,
}: {
    job: Job;
    onDelete: () => void;
    onUpdate: (id: string, status: Job["status"], message?: string, progress?: number) => void;
}) {
    const [duration, setDuration] = useState("0s");
    const progress = typeof job.progress === "number" ? Math.max(0, Math.min(100, job.progress)) : null;

    useEffect(() => {
        const interval = setInterval(() => {
            const seconds = Math.floor((Date.now() - job.startTime) / 1000);
            if (seconds < 60) setDuration(`${seconds}s`);
            else setDuration(`${Math.floor(seconds / 60)}m ${seconds % 60}s`);
        }, 1000);
        return () => clearInterval(interval);
    }, [job.startTime]);

    useEffect(() => {
        if (!job.id.startsWith("procurement-") || job.status !== "running") return;

        const poll = async () => {
            try {
                const latest = await api.getProcurementJob(job.id);
                const status = procurementStatusToUi(latest.status);
                onUpdate(job.id, status, latest.message, latest.progress);

                if (latest.status === "completed") {
                    window.dispatchEvent(new CustomEvent("procurement-refresh-completed"));
                    window.setTimeout(onDelete, 1200);
                }
            } catch {
                onUpdate(job.id, "failed", "Unable to confirm job status", progress ?? undefined);
            }
        };

        void poll();
        const interval = window.setInterval(() => void poll(), 2500);
        return () => window.clearInterval(interval);
    }, [job.id, job.status, onDelete, onUpdate, progress]);

    return (
        <div className="bg-background border border-border rounded-lg p-3 flex items-center justify-between group hover:border-primary/50 transition-colors">
            <div className="flex items-start gap-3 overflow-hidden">
                <div className={cn(
                    "p-2 rounded-full shrink-0",
                    job.status === 'running' ? "bg-primary/10 text-primary" :
                        job.status === 'failed' ? "bg-destructive/10 text-destructive" : "bg-gray-100 text-gray-500"
                )}>
                    {job.status === 'running' ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4" />}
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-medium truncate capitalize text-foreground" title={job.id}>{job.type.replace('-', ' ')}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground font-mono flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {duration}
                        </span>
                        {job.message && <span className="text-xs text-muted-foreground truncate max-w-[140px]">{job.message}</span>}
                    </div>
                    {progress !== null && (
                        <div className="mt-2 h-1.5 w-full min-w-[180px] rounded-full bg-muted overflow-hidden">
                            <div
                                className="h-full rounded-full bg-primary transition-all duration-500"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    )}
                </div>
            </div>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    if (job.onCancel && job.status === 'running') {
                        void job.onCancel(job.id);
                    } else {
                        onDelete();
                    }
                }}
                className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                title={job.status === 'running' ? "Cancel job" : "Remove job"}
            >
                <Trash2 className="w-4 h-4" />
            </button>
        </div>
    );
}
